import { useSyncExternalStore } from 'react';

/**
 * Shared gating for the session-start prompt hosts (What's New popup, app-install
 * badge, location opt-in). All suppress on playback routes and must not stack:
 * What's New holds the slot first (see {@link useWhatsNewHoldsPromptSlot}); on
 * mobile the app badge then shows before the location prompt, which waits until
 * the badge is closed (signalled by {@link APP_BADGE_CLOSED_EVENT}).
 */

const SUPPRESSED_PREFIXES: readonly string[] = [
  '/videos/',
  '/video-player/',
  '/series-player/',
  '/shorts/',
  '/trailer/',
];

const SUPPRESSED_REGEX: readonly RegExp[] = [
  /^\/courses\/[^/]+\/play(\/|$)/,
  /^\/series\/[^/]+\/play(\/|$)/,
  // /[handler]/videos/* and /[handler]/shorts/* — single-segment creator handle
  // followed by a player surface. Excludes the studio top-level.
  /^\/(?!studio(\/|$))[^/]+\/(videos|shorts)(\/|$)/,
];

/** True on video / shorts / course / series playback routes. */
export function isPlaybackRoute(pathname: string): boolean {
  if (SUPPRESSED_PREFIXES.some((p) => pathname.startsWith(p))) return true;
  return SUPPRESSED_REGEX.some((r) => r.test(pathname));
}

/**
 * What's New prompt slot, per page session:
 * - idle: no What's New host is deciding (guests, flag off)
 * - pending: the host is fetching / waiting to leave a playback route
 * - claimed: the popup showed — it keeps the slot for the rest of the session
 * - released: nothing to show — the other prompts are free
 */
export type WhatsNewSlot = 'idle' | 'pending' | 'claimed' | 'released';

let whatsNewSlot: WhatsNewSlot = 'idle';
const slotListeners = new Set<() => void>();

/**
 * Host-only. `claimed` and `released` are terminal until reload: one prompt per
 * session, and a released slot never re-blocks a badge that may already be up.
 */
export function setWhatsNewSlot(next: WhatsNewSlot): void {
  if (whatsNewSlot === 'claimed' || whatsNewSlot === 'released' || whatsNewSlot === next) return;
  whatsNewSlot = next;
  slotListeners.forEach((listener) => listener());
}

export function getWhatsNewSlot(): WhatsNewSlot {
  return whatsNewSlot;
}

function subscribeWhatsNewSlot(listener: () => void): () => void {
  slotListeners.add(listener);
  return () => slotListeners.delete(listener);
}

/** True while What's New is deciding or has shown — other prompt hosts stay hidden. */
export function useWhatsNewHoldsPromptSlot(): boolean {
  const slot = useSyncExternalStore(subscribeWhatsNewSlot, getWhatsNewSlot, () => 'idle' as const);
  return slot === 'pending' || slot === 'claimed';
}

let whatsNewOpen = false;

/** Host-only: the popup is on screen (true) or has closed (false). */
export function setWhatsNewOpen(next: boolean): void {
  if (whatsNewOpen === next) return;
  whatsNewOpen = next;
  slotListeners.forEach((listener) => listener());
}

function getWhatsNewBlocksTour(): boolean {
  return whatsNewSlot === 'pending' || whatsNewOpen;
}

/**
 * True while What's New is deciding or its popup is on screen. Unlike
 * {@link useWhatsNewHoldsPromptSlot}, a closed popup stops blocking: a tour is
 * the user's next step, not another session-start prompt.
 */
export function useWhatsNewBlocksTour(): boolean {
  return useSyncExternalStore(subscribeWhatsNewSlot, getWhatsNewBlocksTour, () => false);
}

/** Test-only reset. */
export function resetWhatsNewSlotForTests(): void {
  whatsNewSlot = 'idle';
  whatsNewOpen = false;
}

/** Dispatched on `window` when the app-install badge is dismissed. */
export const APP_BADGE_CLOSED_EVENT = 'tt:appbadge-closed';

export const APP_BADGE_DISMISS_COOKIE = 'tt_appbadge_dismissed';

// Location opt-in cookies, shared by the prompt host and the /account toggle.
export const LOCATION_SHARED_COOKIE = 'tt_location_shared';
export const LOCATION_DISMISSED_COOKIE = 'tt_location_dismissed';
export const LOCATION_SHARED_MAX_AGE = 60 * 60 * 24 * 365; // 1 year — sticky opt-in
export const LOCATION_DISMISSED_MAX_AGE = 60 * 60 * 24 * 7; // 7 days — re-ask cooldown

/** SSR-safe presence check for a cookie by name. */
export function hasCookie(name: string): boolean {
  if (typeof document === 'undefined') return false;
  return new RegExp(`(?:^|;\\s*)${name}=`).test(document.cookie);
}

/** Write a session-scoped cookie (`path=/`, `samesite=lax`). */
export function writeCookie(name: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=1; path=/; max-age=${maxAgeSeconds}; samesite=lax`;
}

/** Expire a cookie immediately. */
export function expireCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; path=/; max-age=0; samesite=lax`;
}

export interface Coords {
  latitude: number;
  longitude: number;
}

/** Promise wrapper around the one-shot geolocation request. */
export function requestGeolocation(): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 600_000 }
    );
  });
}
